import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeader from "./PageHeader";

// Icons
const DropIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>;
const AlertIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const PdfIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>;

const getFatPriceKey = (title) => `${title}_fatPrices`;

const AnimalPriceGrid = ({ tableTitle }) => {
  const isCow = tableTitle.toLowerCase().includes("cow");

  // Matrix Constants
  const startFat = 3.5, endFat = 10.0;
  const startSnf = 7.0, endSnf = 10.0;
  const numCols = Math.round((endFat - startFat) / 0.1) + 1;
  const numRows = Math.round((endSnf - startSnf) / 0.1) + 1;

  const [prices, setPrices] = useState(() => JSON.parse(localStorage.getItem(tableTitle)) || []);
  const [fatPrices, setFatPrices] = useState(() => {
    const stored = localStorage.getItem(getFatPriceKey(tableTitle));
    return stored ? JSON.parse(stored) : isCow ? { below35: "", above35: "" } : { fat100: "" };
  });
  
  // Initialize with saved values to prevent reset on reload
  const [priceFat100, setPriceFat100] = useState(() => localStorage.getItem(`${tableTitle}_priceFat100`) || "");
  const [priceSnf100, setPriceSnf100] = useState(() => localStorage.getItem(`${tableTitle}_priceSnf100`) || "");
  const [hasChanges, setHasChanges] = useState(false);
  const [pdfReady, setPdfReady] = useState(prices.length > 0);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // Updated to capture all inputs and save to local storage
  const handleSave = (newPrices = prices, newFatPrices = fatPrices, newFat100 = priceFat100, newSnf100 = priceSnf100) => {
    localStorage.setItem(tableTitle, JSON.stringify(newPrices));
    localStorage.setItem(getFatPriceKey(tableTitle), JSON.stringify(newFatPrices));
    localStorage.setItem(`${tableTitle}_priceFat100`, newFat100);
    localStorage.setItem(`${tableTitle}_priceSnf100`, newSnf100);
    setHasChanges(false);
    toast.success("Pricing Saved Successfully!");
  };

  const generateTable = () => {
    if (!priceFat100 || !priceSnf100) return toast.error("Enter Fat & SNF rates");
    
    const generated = Array.from({ length: numRows }, (_, y) => {
      const snfValue = startSnf + y * 0.1;
      return Array.from({ length: numCols }, (_, x) => {
        const fatValue = startFat + x * 0.1;
        return ((fatValue * parseFloat(priceFat100)) / 10 + (snfValue * parseFloat(priceSnf100)) / 10).toFixed(2);
      });
    });
    
    setPrices(generated);
    setPdfReady(true);
    
    // Auto-Save magic: As soon as they generate, it saves locally so they can't forget
    handleSave(generated, fatPrices, priceFat100, priceSnf100);
  };

  const updateFatPrice = (updates) => {
    setFatPrices(prev => ({ ...prev, ...updates }));
    setHasChanges(true); // Triggers the sticky bottom bar
  };

  const handlePrintPdf = () => {
    if (!prices || prices.length === 0) {
      toast.error("Please generate the chart first.");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Popup blocked. Please allow popups.");
      return;
    }

    let tableHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${tableTitle} Rate Chart</title>
        <style>
          @page { size: landscape; margin: 8mm; }
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; margin: 0; padding: 0; font-size: 8px; color: #1F3A5F; }
          h2 { text-align: center; font-size: 16px; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 1px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #E1DBC9; padding: 3px 2px; text-align: center; }
          th { background-color: #F6F3EC; font-weight: bold; color: #1F3A5F; }
          .snf-col { background-color: #F6F3EC; font-weight: bold; }
          .footer { margin-top: 10px; text-align: center; font-size: 9px; color: #6B7280; }
        </style>
      </head>
      <body>
        <h2>${isCow ? "Cow" : "Buffalo"} Rate Matrix Chart</h2>
        <table>
          <thead>
            <tr>
              <th>SNF \\ FAT</th>
    `;

    for (let i = 0; i < numCols; i++) {
      tableHtml += `<th>${(startFat + i * 0.1).toFixed(1)}</th>`;
    }
    tableHtml += `</tr></thead><tbody>`;

    prices.forEach((row, y) => {
      tableHtml += `<tr><td class="snf-col">${(startSnf + y * 0.1).toFixed(1)}</td>`;
      row.forEach(price => {
        tableHtml += `<td>${price ? price : '-'}</td>`;
      });
      tableHtml += `</tr>`;
    });

    tableHtml += `
          </tbody>
        </table>
        <div class="footer">Generated on ${new Date().toLocaleDateString()} &bull; 100% FAT: ₹${priceFat100 || '-'} &bull; 100% SNF: ₹${priceSnf100 || '-'}</div>
      </body>
      </html>
    `;

    printWindow.document.write(tableHtml);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="min-h-screen bg-paper pb-32 w-full font-sans text-primary relative">
      <ToastContainer position="top-center" autoClose={1500} hideProgressBar theme="colored" />

      {/* HEADER - Removed the easily missed top-right save button */}
      <div className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md border-b border-line px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto w-full">
          <PageHeader title={isCow ? "Cow Rate" : "Buffalo Rate"} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 mt-6 space-y-6">

        {/* --- MAIN FAT INPUTS --- */}
        <section className="bg-white rounded-2xl shadow-sm border border-line overflow-hidden">
          <div className="bg-[#B8862E]/10 border-b border-[#B8862E]/20 px-4 py-3 flex items-center gap-2">
            <div className="text-[#B8862E]"><DropIcon /></div>
            <span className="text-[#B8862E] font-bold text-[11px] uppercase tracking-widest">Main Fat Pricing</span>
          </div>
          <div className="p-4">
            {isCow ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FatInput label="Fat < 3.5 Rate" value={fatPrices.below35} onChange={(v) => updateFatPrice({ below35: v })} />
                <FatInput label="Fat > 3.5 Rate" value={fatPrices.above35} onChange={(v) => updateFatPrice({ above35: v })} />
              </div>
            ) : (
              <FatInput label="Standard 100% Fat Price" value={fatPrices.fat100} onChange={(v) => updateFatPrice({ fat100: v })} />
            )}
          </div>
        </section>

        {/* --- MATRIX GENERATOR --- */}
        <section className="bg-white rounded-2xl shadow-sm border border-line p-4">
          <h3 className="text-[10px] font-bold text-[#6B7280] uppercase tracking-widest mb-4 ml-0.5">Bulk Matrix Generator</h3>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <FatInput label="100% Fat Rate" value={priceFat100} onChange={(val) => { setPriceFat100(val); setHasChanges(true); }} />
            <FatInput label="100% SNF Rate" value={priceSnf100} onChange={(val) => { setPriceSnf100(val); setHasChanges(true); }} />
          </div>
          
          <button 
            onClick={generateTable} 
            className="w-full py-3.5 bg-paper border border-line text-primary rounded-xl font-bold text-[14px] active:scale-[0.98] hover:border-primary/50 transition-all shadow-sm"
          >
            Generate Matrix
          </button>
        </section>

        {/* --- PDF READY CARD --- */}
        {pdfReady && (
          <section className="bg-credit/5 border border-credit/20 rounded-2xl p-5 shadow-sm flex flex-col items-center text-center animate-fade-in">
            <div className="bg-white p-3 rounded-full border border-credit/20 text-credit mb-3 shadow-sm">
              <PdfIcon />
            </div>
            <h3 className="font-bold text-primary text-[16px] mb-1">Rate Chart Ready</h3>
            <p className="text-[12px] text-[#6B7280] font-medium mb-5">
              Your matrix has been generated successfully. You can now view, download, or print the PDF.
            </p>
            
            <button 
              onClick={handlePrintPdf}
              className="w-full py-3.5 bg-primary text-white rounded-xl font-slab font-bold text-[14px] tracking-wide active:scale-[0.98] shadow-sm transition-all"
            >
              View / Download PDF
            </button>
          </section>
        )}
      </div>

      {/* --- FLOATING UNSAVED CHANGES BAR --- */}
      {hasChanges && (
        <div className="fixed bottom-6 left-0 w-full px-4 z-[100] animate-bounce-up">
          <div className="max-w-lg mx-auto bg-primary rounded-2xl p-3 shadow-2xl flex items-center justify-between border border-primary/20">
            <div className="flex items-center gap-2.5 ml-2">
              <div className="w-2 h-2 rounded-full bg-amber animate-pulse"></div>
              <span className="text-white font-bold text-[13px] tracking-wide">Unsaved Rates</span>
            </div>
            <button 
              onClick={() => handleSave()} 
              className="bg-white text-primary px-5 py-2.5 rounded-xl font-bold text-[13px] active:scale-95 shadow-sm transition-all"
            >
              Save Pricing
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

// Internal Custom Input tailored for the Fat settings
const FatInput = ({ label, value, onChange, placeholder = "0.00" }) => {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[10px] font-bold text-primary uppercase tracking-widest ml-0.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-[14px] text-[#6B7280]">₹</span>
        <input 
          type="number" 
          inputMode="decimal" 
          value={value} 
          onChange={(e) => onChange(e.target.value)} 
          className="w-full pl-8 pr-4 py-3 bg-white border border-line rounded-xl text-[13.5px] font-mono font-bold text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all shadow-sm" 
          placeholder={placeholder} 
        />
      </div>
    </div>
  );
};

export default AnimalPriceGrid;